/**
 * The source selection of one Q&A as a set of items: folders and documents
 * (spec §6). A selected folder stands for everything below it, now and later
 * (the server expands selected folders again at every send), so the set is
 * kept normalized: no item is stored when a selected folder above it already
 * covers it.
 *
 * Everything here is pure. Folder contents come from the caller, either as a
 * synchronous lookup of resolved descendants or, for splitting a folder, as
 * an async tree resolver.
 */

export type ItemKind = 'folder' | 'file';

/** `folder:12` or `file:34`. */
export type ItemKey = string;

export interface ItemRef {
  kind: ItemKind;
  id: string;
}

export type SelectionState = 'on' | 'mixed' | 'off';

export interface Descendants {
  /** Every folder below the folder, at any depth. */
  folderIds: string[];
  /** Every ready document below the folder, at any depth. */
  fileIds: string[];
}

/** Resolved descendants of a folder, or undefined while unknown. */
export type DescendantsOf = (folderId: string) => Descendants | undefined;

export interface FolderChildren {
  folderIds: string[];
  /** Every document directly in the folder, whatever its processing state. */
  fileIds: string[];
}

/** The async tree lookups splitting a folder needs. */
export interface TreeResolver {
  children: (folderId: string) => Promise<FolderChildren>;
  descendants: (folderIds: string[]) => Promise<Map<string, Descendants>>;
}

export const itemKey = (kind: ItemKind, id: string | number): ItemKey =>
  `${kind}:${id}`;

export const parseItemKey = (key: ItemKey): ItemRef => {
  const at = key.indexOf(':');
  return {
    kind: key.slice(0, at) === 'folder' ? 'folder' : 'file',
    id: key.slice(at + 1),
  };
};

export const keyOf = (ref: ItemRef): ItemKey => itemKey(ref.kind, ref.id);

const folderIdsOf = (draft: ReadonlySet<ItemKey>): string[] =>
  [...draft].flatMap(key => {
    const ref = parseItemKey(key);
    return ref.kind === 'folder' ? [ref.id] : [];
  });

const fileIdsOf = (draft: ReadonlySet<ItemKey>): string[] =>
  [...draft].flatMap(key => {
    const ref = parseItemKey(key);
    return ref.kind === 'file' ? [ref.id] : [];
  });

/** Selected folders whose descendants are not resolved yet. */
export const unresolvedFolders = (
  draft: ReadonlySet<ItemKey>,
  descendantsOf: DescendantsOf,
): string[] => folderIdsOf(draft).filter(id => !descendantsOf(id));

export interface Cover {
  /** Folders below a selected folder (not the selected folders themselves). */
  folders: Set<string>;
  /** Ready documents below a selected folder. */
  files: Set<string>;
}

/** Everything the selected folders stand for, as far as it is resolved. */
export const coverOf = (
  draft: ReadonlySet<ItemKey>,
  descendantsOf: DescendantsOf,
): Cover => {
  const cover: Cover = { folders: new Set(), files: new Set() };
  folderIdsOf(draft).forEach(id => {
    const resolved = descendantsOf(id);
    resolved?.folderIds.forEach(folderId => cover.folders.add(folderId));
    resolved?.fileIds.forEach(fileId => cover.files.add(fileId));
  });
  return cover;
};

/**
 * Whether an item counts as selected: itself, or through a selected folder.
 * `ancestors` are the folder ids above the item when the caller knows them
 * (a browsed folder's breadcrumb); they also cover documents the descendant
 * lookup leaves out because they are not processed yet.
 */
export const isCovered = (
  ref: ItemRef,
  draft: ReadonlySet<ItemKey>,
  cover: Cover,
  ancestors: readonly string[] = [],
): boolean =>
  draft.has(keyOf(ref)) ||
  (ref.kind === 'folder'
    ? cover.folders.has(ref.id)
    : cover.files.has(ref.id)) ||
  ancestors.some(id => draft.has(itemKey('folder', id)));

/**
 * A folder is on when it is covered, mixed when it is not but something
 * below it is selected, and off otherwise. A document is on or off.
 */
export const stateOf = (
  ref: ItemRef,
  draft: ReadonlySet<ItemKey>,
  cover: Cover,
  descendantsOf: DescendantsOf,
  ancestors: readonly string[] = [],
): SelectionState => {
  if (isCovered(ref, draft, cover, ancestors)) return 'on';
  if (ref.kind === 'file') return 'off';
  const inside = descendantsOf(ref.id);
  if (!inside) return 'off';
  const hasSelectedInside =
    inside.folderIds.some(id => draft.has(itemKey('folder', id))) ||
    inside.fileIds.some(id => draft.has(itemKey('file', id)));
  return hasSelectedInside ? 'mixed' : 'off';
};

/**
 * Drop every item a selected folder already covers. Folders whose
 * descendants are unknown are kept as they are, and so is what may lie
 * below them, until they resolve.
 */
export const normalize = (
  draft: ReadonlySet<ItemKey>,
  descendantsOf: DescendantsOf,
): Set<ItemKey> => {
  const cover = coverOf(draft, descendantsOf);
  return new Set(
    [...draft].filter(key => {
      const ref = parseItemKey(key);
      return ref.kind === 'folder'
        ? !cover.folders.has(ref.id)
        : !cover.files.has(ref.id);
    }),
  );
};

/** Select an item: it replaces whatever it covers; covered items stay as they are. */
export const selectItem = (
  draft: ReadonlySet<ItemKey>,
  ref: ItemRef,
  descendantsOf: DescendantsOf,
  ancestors: readonly string[] = [],
): Set<ItemKey> => {
  if (isCovered(ref, draft, coverOf(draft, descendantsOf), ancestors)) {
    return new Set(draft);
  }
  return normalize(new Set([...draft, keyOf(ref)]), descendantsOf);
};

/** Select several items at once (a page, or all results). */
export const selectItems = (
  draft: ReadonlySet<ItemKey>,
  refs: readonly ItemRef[],
  descendantsOf: DescendantsOf,
): Set<ItemKey> => {
  const cover = coverOf(draft, descendantsOf);
  const additions = refs
    .filter(ref => !isCovered(ref, draft, cover))
    .map(keyOf);
  return normalize(new Set([...draft, ...additions]), descendantsOf);
};

/** The selected folder that covers the item, if any. */
const coveringFolder = (
  ref: ItemRef,
  draft: ReadonlySet<ItemKey>,
  descendantsOf: DescendantsOf,
  ancestors: readonly string[],
): string | undefined => {
  const fromAncestors = ancestors.find(id => draft.has(itemKey('folder', id)));
  if (fromAncestors) return fromAncestors;
  return folderIdsOf(draft).find(id => {
    const inside = descendantsOf(id);
    if (!inside) return false;
    return ref.kind === 'folder'
      ? inside.folderIds.includes(ref.id)
      : inside.fileIds.includes(ref.id);
  });
};

const containsRef = (inside: Descendants | undefined, ref: ItemRef) =>
  !!inside &&
  (ref.kind === 'folder'
    ? inside.folderIds.includes(ref.id)
    : inside.fileIds.includes(ref.id));

/**
 * The folders from just below `top` down to the item's parent, found by
 * walking the tree: at each level, the child folder whose descendants hold
 * the item.
 */
const pathBelow = async (
  top: string,
  ref: ItemRef,
  resolver: TreeResolver,
): Promise<string[] | null> => {
  const path: string[] = [];
  let level = top;
  // A tree deeper than this is a cycle in bad data.
  for (let depth = 0; depth < 64; depth += 1) {
    const kids = await resolver.children(level);
    const direct =
      ref.kind === 'folder'
        ? kids.folderIds.includes(ref.id)
        : kids.fileIds.includes(ref.id);
    if (direct) return path;
    if (kids.folderIds.length === 0) return null;
    const below = await resolver.descendants(kids.folderIds);
    const next = kids.folderIds.find(id => containsRef(below.get(id), ref));
    if (!next) return null;
    path.push(next);
    level = next;
  }
  return null;
};

/**
 * Deselect an item. A directly selected item is removed. An item covered by
 * a selected folder splits that folder: the folder is replaced by everything
 * inside it except the branch leading to the item, level by level, so the
 * rest stays selected as individual items (spec §6). A mixed folder loses
 * the selected items inside it.
 *
 * `ancestors` (top-down folder ids above the item, when known) saves the
 * tree walk. Returns the draft unchanged when the walk cannot place the item.
 */
export const deselectItem = async (
  draft: ReadonlySet<ItemKey>,
  ref: ItemRef,
  descendantsOf: DescendantsOf,
  resolver: TreeResolver,
  ancestors: readonly string[] = [],
): Promise<Set<ItemKey>> => {
  const key = keyOf(ref);
  if (draft.has(key)) {
    const next = new Set(draft);
    next.delete(key);
    return next;
  }

  const top = coveringFolder(ref, draft, descendantsOf, ancestors);
  if (!top) {
    if (ref.kind === 'file') return new Set(draft);
    const inside = descendantsOf(ref.id);
    if (!inside) return new Set(draft);
    const drop = new Set([
      ...inside.folderIds.map(id => itemKey('folder', id)),
      ...inside.fileIds.map(id => itemKey('file', id)),
    ]);
    return new Set([...draft].filter(item => !drop.has(item)));
  }

  const known = ancestors.indexOf(top);
  const path =
    known >= 0
      ? ancestors.slice(known + 1)
      : await pathBelow(top, ref, resolver);
  if (path === null) return new Set(draft);

  const next = new Set(draft);
  next.delete(itemKey('folder', top));
  let level = top;
  for (const node of [...path, ref.id]) {
    const kids = await resolver.children(level);
    const isTarget = node === ref.id;
    kids.folderIds.forEach(id => {
      if (id !== node || (isTarget && ref.kind === 'file')) {
        next.add(itemKey('folder', id));
      }
    });
    kids.fileIds.forEach(id => {
      if (!(isTarget && ref.kind === 'file' && id === node)) {
        next.add(itemKey('file', id));
      }
    });
    level = node;
  }
  return normalize(next, descendantsOf);
};

/** Ids to persist: selected folders, and documents with the folders' current contents. */
export const toPayload = (
  draft: ReadonlySet<ItemKey>,
  descendantsOf: DescendantsOf,
): { folder_ids: string[]; file_ids: string[] } => {
  const files = new Set(fileIdsOf(draft));
  // The expanded documents keep readers that only look at file ids (the old
  // frontend, legacy endpoints) consistent with the folder selection.
  coverOf(draft, descendantsOf).files.forEach(id => files.add(id));
  return {
    folder_ids: folderIdsOf(draft).sort(byNumber),
    file_ids: [...files].sort(byNumber),
  };
};

const byNumber = (a: string, b: string) => Number(a) - Number(b);

/**
 * The stored selection as items: the selected folders, plus the documents
 * the folders do not cover.
 */
export const fromStored = (
  folderIds: readonly string[],
  fileIds: readonly string[],
  descendantsOf: DescendantsOf,
): Set<ItemKey> =>
  normalize(
    new Set([
      ...folderIds.map(id => itemKey('folder', id)),
      ...fileIds.map(id => itemKey('file', id)),
    ]),
    descendantsOf,
  );

/**
 * Replay local changes onto a newer stored selection: what this surface
 * added since the last confirmed state is added, what it removed is removed.
 */
export const mergeDrafts = (
  server: ReadonlySet<ItemKey>,
  lastConfirmed: ReadonlySet<ItemKey>,
  local: ReadonlySet<ItemKey>,
  descendantsOf: DescendantsOf,
): Set<ItemKey> => {
  const merged = new Set(server);
  local.forEach(key => {
    if (!lastConfirmed.has(key)) merged.add(key);
  });
  lastConfirmed.forEach(key => {
    if (!local.has(key)) merged.delete(key);
  });
  return normalize(merged, descendantsOf);
};

/**
 * Items added and removed since the dialog opened, as the user sees them
 * rather than as set entries:
 * - an item of the selection now counts as added only when it was not
 *   selected at opening, neither itself nor through a selected folder;
 * - an item of the selection at opening counts as removed only when it is
 *   not selected now, neither itself nor through a selected folder.
 * So splitting a selected folder (taking one thing out of it) counts as one
 * removal, the folder, and the items that replace it are no additions,
 * since they were selected already. Selecting a folder over items selected
 * one by one counts as one addition. A split folder stays one removal
 * however many things are later taken out of it; folders whose contents
 * are unknown count by their entry alone.
 */
export const diffSince = (
  initial: ReadonlySet<ItemKey>,
  draft: ReadonlySet<ItemKey>,
  descendantsOf: DescendantsOf,
): { added: number; removed: number } => {
  const initialCover = coverOf(initial, descendantsOf);
  const draftCover = coverOf(draft, descendantsOf);
  let added = 0;
  let removed = 0;
  draft.forEach(key => {
    if (!isCovered(parseItemKey(key), initial, initialCover)) added += 1;
  });
  initial.forEach(key => {
    if (!isCovered(parseItemKey(key), draft, draftCover)) removed += 1;
  });
  return { added, removed };
};

export const sameDraft = (
  left: ReadonlySet<ItemKey>,
  right: ReadonlySet<ItemKey>,
): boolean =>
  left.size === right.size && [...left].every(key => right.has(key));
